import { X } from 'lucide-react';
import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

export default function Modal({ isOpen, onClose, title, children }) {
  const bodyRef = useRef(null);
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);
  const firstFocusableRef = useRef(null);

  // Focus trap - keeps focus within modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }

    if (e.key !== 'Tab' || !modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Shift+Tab from first element goes to last
    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement?.focus();
    }
    // Tab from last element goes to first
    else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement?.focus();
    }
  }, [onClose]);

  // Lock body scroll, set focus, and add event listeners
  useEffect(() => {
    if (isOpen) {
      // Store the previously focused element
      previousActiveElement.current = document.activeElement;

      // Set CSS variable for actual viewport height (handles iOS Safari)
      const setVH = () => {
        document.documentElement.style.setProperty('--vh', `${window.innerHeight}px`);
      };
      setVH();

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;

      window.addEventListener('resize', setVH);
      window.addEventListener('orientationchange', setVH);
      document.addEventListener('keydown', handleKeyDown);

      // Scroll modal body to top
      if (bodyRef.current) {
        bodyRef.current.scrollTop = 0;
      }

      // Focus the first focusable element
      requestAnimationFrame(() => {
        if (modalRef.current) {
          const firstFocusable = modalRef.current.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          firstFocusable?.focus();
        }
      });

      return () => {
        window.removeEventListener('resize', setVH);
        window.removeEventListener('orientationchange', setVH);
        document.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);

      // Return focus to previously focused element
      if (previousActiveElement.current && typeof previousActiveElement.current.focus === 'function') {
        previousActiveElement.current.focus();
      }
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999]"
      style={{ height: 'var(--vh, 100vh)' }}
      role="presentation"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal wrapper - uses CSS grid for perfect centering */}
      <div
        className="absolute inset-0 grid place-items-center p-4 overflow-hidden"
        style={{ height: 'var(--vh, 100vh)' }}
      >
        {/* Modal */}
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          className="relative w-full max-w-[520px] bg-surface-card rounded-2xl border border-border shadow-[var(--shadow-elevated)] flex flex-col animate-scale"
          style={{ maxHeight: 'calc(var(--vh, 100vh) - 32px)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-surface-elevated rounded-t-2xl flex-shrink-0">
            <h3 id="modal-title" className="m-0 text-lg font-semibold text-text-primary">{title}</h3>
            <button
              onClick={onClose}
              className="p-2 -mr-2 rounded-lg bg-transparent border-none cursor-pointer text-text-tertiary hover:text-text-primary hover:bg-surface-hover transition-all"
              aria-label="Close modal"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>

          {/* Body - scrollable */}
          <div
            ref={bodyRef}
            className="flex-1 overflow-y-auto overscroll-contain p-5"
            style={{ minHeight: 0 }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render at document.body level
  return createPortal(modalContent, document.body);
}
